/**
 * Manager Approval Modal
 *
 * iPad-friendly interface for managers to review and approve/deny discount requests
 * that require approval due to fraud detection triggers.
 *
 * Features:
 * - Real-time approval requests from discountApprovalService
 * - Fraud analysis details (severity, risk score, flags)
 * - Barber strike history
 * - Quick approve/deny actions
 * - Reason required for denial
 * - Push notifications for new requests
 * - Auto-refresh pending requests
 *
 * Related: discountApprovalService.ts, checkoutInterceptor.ts
 */

import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, XCircle, Clock, DollarSign, User } from 'lucide-react';
import {
  discountApprovalService,
  DiscountApprovalRequest,
  ApprovalDecision,
  DEFAULT_STRIKE_CONFIG
} from '../services/discountApprovalService';
import { useAuth } from '../contexts/AuthContext';

interface ManagerApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Optional: pass a specific request ID to review
  requestId?: string;
}

/**
 * Manager Approval Modal Component
 */
const ManagerApprovalModal: React.FC<ManagerApprovalModalProps> = ({
  isOpen,
  onClose,
  requestId
}) => {
  const { user } = useAuth();
  const [pendingRequests, setPendingRequests] = useState<DiscountApprovalRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<DiscountApprovalRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [showDenyInput, setShowDenyInput] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load pending requests
  useEffect(() => {
    if (isOpen) {
      loadPendingRequests();
      // Auto-refresh every 10 seconds
      const interval = setInterval(loadPendingRequests, 10000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Load specific request if requestId provided
  useEffect(() => {
    if (requestId && isOpen) {
      loadSpecificRequest(requestId);
    }
  }, [requestId, isOpen]);

  const loadPendingRequests = async () => {
    try {
      setLoading(true);
      const requests = await discountApprovalService.getPendingApprovals();
      setPendingRequests(requests);

      // Auto-select first request if none selected
      if (!selectedRequest && requests.length > 0) {
        setSelectedRequest(requests[0]);
      }
    } catch (err) {
      console.error('Error loading pending requests:', err);
      setError('Failed to load approval requests');
    } finally {
      setLoading(false);
    }
  };

  const loadSpecificRequest = async (id: string) => {
    try {
      const request = await discountApprovalService.getApprovalRequest(id);
      if (request) {
        setSelectedRequest(request);
      }
    } catch (err) {
      console.error('Error loading specific request:', err);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest || !user) return;

    try {
      setActionLoading(true);
      setError(null);

      const decision: ApprovalDecision = {
        requestId: selectedRequest.id,
        approved: true,
        reviewedBy: `${user.firstName} ${user.lastName}`,
        reviewerRole: 'manager' as 'manager' | 'owner',
        notes: 'Manager approval - within policy',
        strikeIssued: false
      };

      await discountApprovalService.reviewApprovalRequest(selectedRequest.id, decision);

      // Remove from pending list
      setPendingRequests(prev => prev.filter(r => r.id !== selectedRequest.id));

      // Select next request or close if none left
      if (pendingRequests.length > 1) {
        const nextRequest = pendingRequests.find(r => r.id !== selectedRequest.id);
        setSelectedRequest(nextRequest || null);
      } else {
        setSelectedRequest(null);
        onClose();
      }
    } catch (err) {
      console.error('Error approving request:', err);
      setError('Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeny = async () => {
    if (!selectedRequest || !user) return;

    if (!denyReason.trim()) {
      setError('Please provide a reason for denial');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      // Determine if we should issue a strike based on severity
      const issueStrike = selectedRequest.fraudAnalysis.severity === 'high' ||
                         selectedRequest.fraudAnalysis.severity === 'critical';

      const decision: ApprovalDecision = {
        requestId: selectedRequest.id,
        approved: false,
        reviewedBy: `${user.firstName} ${user.lastName}`,
        reviewerRole: 'manager' as 'manager' | 'owner',
        notes: denyReason,
        strikeIssued: issueStrike,
        strikeReason: issueStrike ? `Discount denied: ${denyReason}` : undefined
      };

      await discountApprovalService.reviewApprovalRequest(selectedRequest.id, decision
      );

      // Remove from pending list
      setPendingRequests(prev => prev.filter(r => r.id !== selectedRequest.id));

      // Reset deny input
      setShowDenyInput(false);
      setDenyReason('');

      // Select next request or close if none left
      if (pendingRequests.length > 1) {
        const nextRequest = pendingRequests.find(r => r.id !== selectedRequest.id);
        setSelectedRequest(nextRequest || null);
      } else {
        setSelectedRequest(null);
        onClose();
      }
    } catch (err) {
      console.error('Error denying request:', err);
      setError('Failed to deny request');
    } finally {
      setActionLoading(false);
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date: Date | any): string => {
    const d = date instanceof Date ? date : date.toDate();
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[9998]" style={{ backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div className="absolute top-0 right-0 h-[100dvh] w-full max-w-md bg-white shadow-2xl flex flex-col" style={{ animation: 'slideInRight 0.3s ease-out' }} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-6 w-6 text-orange-500" />
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  Manager Approval
                </h2>
                <p className="text-sm text-gray-500">
                  {pendingRequests.length} pending request{pendingRequests.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
            {/* Request List View */}
            {!selectedRequest ? (
              <div>
                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                  </div>
                ) : pendingRequests.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
                    <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
                    <p className="text-lg font-semibold text-gray-700">All Clear!</p>
                    <p className="text-sm text-gray-500 mt-2">
                      No pending approvals at this time
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {pendingRequests.map((request) => (
                      <button
                        key={request.id}
                        onClick={() => setSelectedRequest(request)}
                        className="w-full text-left p-4 transition-colors hover:bg-gray-50"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className="font-semibold text-gray-900">
                            {request.barberName}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full border ${getSeverityColor(request.fraudAnalysis.severity)}`}>
                            {request.fraudAnalysis.severity.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          {request.discountRequest.clientName}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{formatCurrency(request.discountRequest.discountAmount)} off</span>
                          <span>{formatDate(request.requestedAt)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Detail View */
              <div className="p-4 space-y-4">
                {/* Back to list button */}
                {pendingRequests.length > 1 && (
                  <button
                    onClick={() => setSelectedRequest(null)}
                    className="text-sm text-purple-600 hover:text-purple-800 font-medium flex items-center space-x-1"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>All Requests ({pendingRequests.length})</span>
                  </button>
                )}

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                    <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                {/* Fraud Analysis */}
                <div className={`border-2 rounded-xl p-4 ${getSeverityColor(selectedRequest.fraudAnalysis.severity)}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-bold">Fraud Analysis</h3>
                    <span className="text-2xl font-bold">
                      {selectedRequest.fraudAnalysis.riskScore}/100
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">Severity:</span>
                      <span className="uppercase font-semibold">
                        {selectedRequest.fraudAnalysis.severity}
                      </span>
                    </div>
                    {selectedRequest.fraudAnalysis.fraudFlags.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium mb-2">Fraud Flags:</p>
                        <ul className="space-y-1">
                          {selectedRequest.fraudAnalysis.fraudFlags.map((flag, index) => (
                            <li key={index} className="text-sm flex items-start">
                              <span className="mr-2">•</span>
                              <span>{flag}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Transaction Details */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h3 className="text-base font-bold text-gray-900 mb-3">Transaction Details</h3>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Barber</p>
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-semibold text-gray-900">
                          {selectedRequest.barberName}
                        </span>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Client</p>
                      <span className="font-semibold text-gray-900">
                        {selectedRequest.discountRequest.clientName}
                      </span>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Original Price</p>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(selectedRequest.discountRequest.originalPrice)}
                      </span>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Discount</p>
                      <div className="flex items-center space-x-2">
                        <DollarSign className="h-4 w-4 text-red-500" />
                        <span className="font-semibold text-red-600">
                          -{formatCurrency(selectedRequest.discountRequest.discountAmount)}
                          <span className="text-sm ml-1">
                            ({selectedRequest.discountRequest.discountPercent}%)
                          </span>
                        </span>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Final Price</p>
                      <span className="font-semibold text-green-600">
                        {formatCurrency(selectedRequest.discountRequest.discountedPrice)}
                      </span>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Payment Method</p>
                      <span className="font-semibold text-gray-900 capitalize">
                        {selectedRequest.discountRequest.paymentMethod.replace('_', ' ')}
                      </span>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Reason</p>
                      <span className="font-semibold text-gray-900 capitalize">
                        {selectedRequest.discountRequest.discountReason.replace('_', ' ')}
                      </span>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Request Time</p>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="font-semibold text-gray-900">
                          {formatDate(selectedRequest.requestedAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Barber History */}
                <div className="bg-blue-50 rounded-xl p-4">
                  <h3 className="text-base font-bold text-gray-900 mb-3">Barber History</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Strike Count:</span>
                      <span className={`font-bold text-lg ${
                        selectedRequest.discountRequest.barberStrikeCount >= DEFAULT_STRIKE_CONFIG.blockLimit
                          ? 'text-red-600'
                          : selectedRequest.discountRequest.barberStrikeCount >= DEFAULT_STRIKE_CONFIG.autoApproveLimit
                          ? 'text-orange-600'
                          : 'text-green-600'
                      }`}>
                        {selectedRequest.discountRequest.barberStrikeCount} strikes
                      </span>
                    </div>
                    {selectedRequest.requiredApprovalLevel !== 'none' && (
                      <p className="text-sm text-gray-700 bg-white rounded-lg p-3">
                        Approval required: {selectedRequest.requiredApprovalLevel} approval
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer - Action Buttons (only when a request is selected) */}
          {selectedRequest && (
            <div className="flex-shrink-0 border-t border-gray-200 p-4 bg-white pb-safe">
              {!showDenyInput ? (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleApprove}
                    disabled={actionLoading}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle className="h-5 w-5" />
                    <span>{actionLoading ? 'Processing...' : 'Approve'}</span>
                  </button>
                  <button
                    onClick={() => setShowDenyInput(true)}
                    disabled={actionLoading}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircle className="h-5 w-5" />
                    <span>Deny</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <textarea
                    value={denyReason}
                    onChange={(e) => setDenyReason(e.target.value)}
                    placeholder="Enter reason for denial (required)..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    rows={3}
                    autoFocus
                  />
                  <div className="flex space-x-3">
                    <button
                      onClick={handleDeny}
                      disabled={actionLoading || !denyReason.trim()}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? 'Processing...' : 'Confirm Denial'}
                    </button>
                    <button
                      onClick={() => {
                        setShowDenyInput(false);
                        setDenyReason('');
                        setError(null);
                      }}
                      disabled={actionLoading}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default ManagerApprovalModal;
